import { Ref } from 'vue';

import { CommentService, useComments } from '@rygen/rygen-ui/composables';
import {
  UserComment,
  UserCommentSearchFilter,
  UserCommentSearchResult,
  UserCommentWithRepliesSearchResult,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useCurrentUserStore } from '@/stores/CurrentUser.ts';

class ShipmentCommentService implements CommentService {
  constructor(private shipmentId: Ref<number>) {}

  async loadComments(
    searchQuery: UserCommentSearchFilter,
  ): Promise<UserCommentWithRepliesSearchResult> {
    // Comments requires an id, but shipment loads with an `undefined` id or `-1`
    if (this.shipmentId.value <= 0) {
      return new UserCommentWithRepliesSearchResult();
    }
    return api.shipmentComment.findCommentsForShipment(
      this.shipmentId.value,
      searchQuery,
    );
  }

  async createComment(content: Record<string, unknown>): Promise<UserComment> {
    return api.shipmentComment.createComment(this.shipmentId.value, content);
  }

  async updateComment(
    commentId: number,
    content: Record<string, unknown>,
  ): Promise<UserComment> {
    return api.shipmentComment.updateComment(
      this.shipmentId.value,
      commentId,
      content,
    );
  }

  async replyToComment(
    commentId: number,
    content: Record<string, unknown>,
  ): Promise<UserComment> {
    return api.shipmentComment.replyToComment(
      this.shipmentId.value,
      commentId,
      content,
    );
  }

  async deleteComment(commentId: number): Promise<void> {
    return api.shipmentComment.deleteComment(this.shipmentId.value, commentId);
  }

  async loadReplies(
    commentId: number,
    searchQuery: UserCommentSearchFilter,
  ): Promise<UserCommentSearchResult> {
    return api.shipmentComment.findRepliesForComment(
      this.shipmentId.value,
      commentId,
      searchQuery,
    );
  }

  async loadCommentsCount(): Promise<number> {
    // Comments requires an id, but shipment loads with an `undefined` id or `-1`
    if (this.shipmentId.value <= 0) {
      return 0;
    }
    return api.shipmentComment.findCommentCountForShipment(
      this.shipmentId.value,
    );
  }
}

export const useShipmentComments = (shipmentId: Ref<number>) => {
  const { handleRequiredDomainRedirect } = useCurrentUserStore();

  const commentService = new ShipmentCommentService(shipmentId);

  return useComments(
    commentService,
    shipmentId,
    'Shipment',
    handleRequiredDomainRedirect,
  );
};
